import {MouseEvent, MouseEventHandler, useMemo, useState} from 'react';
import {ThemeProvider, styled, useTheme} from 'styled-components';
import {LinkProps, Link as WooterLink} from 'wouter';

import {capitalize} from '@shared/lib/utils/format_utils';

import {mergeTheme} from '@shared_web/components/core/_base';
import {LoadingIndicator} from '@shared_web/components/core/loading_indicator';
import {Overlay} from '@shared_web/components/core/overlay';
import {cssGap} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';
import {Custom} from '@shared_web/lib/react';
import {optional, optionalPx, optionalRaw} from '@shared_web/lib/styled_utils';
import {TransitionDuration, cssTransition} from '@shared_web/lib/transitions';
import {useGlobalKeyPress} from '@shared_web/lib/use_global_key_press';
import {ZINDEX} from '@shared_web/lib/zindex';
import {ButtonTheme} from '@shared_web/theme/theme_model';

type ButtonThemeType = 'button' | 'link' | 'unthemed';

export interface ButtonProps {
  disabled?: boolean;
  loading?: boolean;
  onClickAsync?: (evt: MouseEvent<HTMLElement>) => Promise<unknown>;
  onClick?: MouseEventHandler;
  expand?: boolean;
  hidden?: boolean;
  themeOverrides?: Partial<ButtonTheme>;
  submit?: boolean;
  keyboardSubmit?: boolean;
  flexGap?: number | string;
}

interface ButtonTypes {
  button: Custom<ButtonProps, 'button'>;
  a: Custom<ButtonProps, 'a'>;
  NavLink: Custom<ButtonProps & LinkProps, 'a'>;
}

const underlyingComponent: Record<keyof ButtonTypes, string | typeof WooterLink> = {
  button: 'button',
  a: 'a',
  NavLink: WooterLink,
};

export function createButton<Type extends keyof ButtonTypes>(opts: {
  type: Type;
  themeType: ButtonThemeType;
  themeOverrides?: Partial<ButtonTheme>;
  fromScratch?: boolean;
}): ButtonTypes[Type] {
  const {type, themeType, themeOverrides, fromScratch} = opts;
  type Props = Parameters<ButtonTypes[Type]>[0];
  const ButtonClass: ButtonTypes[Type] = (props: Props) => {
    const {
      disabled = false,
      loading = false,
      expand,
      hidden,
      submit,
      keyboardSubmit,
      themeOverrides: themeOverridesFromProps,
      onClickAsync,
      onClick,
      flexGap,
      ...rest
    } = props;

    // Get theme and add overrides
    const baseTheme = useTheme();
    const themeKey = themeType === 'unthemed' ? (type === 'button' ? 'button' : 'link') : themeType;
    const theme = useMemo(() => {
      const newBaseTheme = {...baseTheme};
      if (themeType === 'unthemed') {
        delete newBaseTheme[themeKey];
      }
      return mergeTheme(
        newBaseTheme,
        {[themeKey]: themeOverrides},
        {[themeKey]: themeOverridesFromProps}
      );
    }, [themeOverridesFromProps, themeKey, baseTheme]);
    const buttonTheme = useMemo(() => theme[themeKey], [theme, themeKey]);

    const [isAsyncClicked, setIsAsyncClicked] = useState(false);
    const onClickCallback = (evt: MouseEvent<HTMLButtonElement>): void => {
      setIsAsyncClicked(true);
      if (onClickAsync !== undefined) {
        onClickAsync(evt)
          .then(() => setIsAsyncClicked(false))
          .catch((err: unknown) => {
            notifyError(err);
            setIsAsyncClicked(false);
          });
      }
    };
    const handleClick = onClickAsync !== undefined ? onClickCallback : onClick;
    const isLoading = isAsyncClicked || loading;
    const isDisabled = disabled;

    useGlobalKeyPress(['Enter'], () => {
      if (keyboardSubmit && !isDisabled && !isLoading) {
        handleClick?.(undefined as unknown as MouseEvent<HTMLButtonElement>);
      }
    });

    if (submit) {
      rest['type'] = 'submit';
    }

    // biome-ignore lint/suspicious/noExplicitAny: <no-explanation>
    const TypeHack = StyledButton as any;
    const button = (
      <TypeHack
        as={underlyingComponent[type]}
        disabled={isDisabled}
        $loading={isLoading}
        tabIndex={isDisabled || isLoading ? '-1' : undefined}
        $expand={expand}
        $hidden={hidden}
        $themeKey={themeKey}
        $flexGap={flexGap}
        onClick={handleClick}
        {...rest}
      />
    );
    if (isLoading) {
      return (
        <ThemeProvider theme={theme}>
          <Overlay
            opacity={0}
            overlayContent={
              <LoadingIndicator
                size={buttonTheme?.loaderSize}
                color={buttonTheme?.loaderColor}
                opacity={buttonTheme?.loaderOpacity}
              />
            }
            element={button}
          />
        </ThemeProvider>
      );
    }
    return <ThemeProvider theme={theme}>{button}</ThemeProvider>;
  };
  let displayNameSuffix = themeOverrides ? `${Object.keys(themeOverrides).length}overrides` : '';
  displayNameSuffix += fromScratch ? '-FromScratch' : '';
  ButtonClass.displayName = `Button-Type${capitalize(type)}-Theme${capitalize(
    themeType
  )}${displayNameSuffix}`;
  return ButtonClass;
}

const StyledButton = styled.button<{
  disabled?: boolean;
  $loading?: boolean;
  $expand?: boolean;
  $hidden?: boolean;
  $themeKey: 'button' | 'link';
  $flexGap?: number;
}>`
  position: relative;
  box-shadow: none;
  outline: none;
  display: ${p => (p.$hidden ? 'none' : 'inline-flex')};
  align-items: center;
  justify-content: space-evenly;
  ${p => cssGap(p.$flexGap)}
  ${p => optionalPx('border-radius', p.theme[p.$themeKey]?.borderRadius)}
  ${p => optionalPx('font-size', p.theme[p.$themeKey]?.fontSize)}
  ${p => optionalPx('letter-spacing', p.theme[p.$themeKey]?.letterSpacing)}
  width: ${p => (p.$expand ? '100%' : 'max-content')};
  ${p => optional('text-decoration', p.theme[p.$themeKey]?.textDecoration)}
  ${p => optionalPx('text-underline-offset', p.theme[p.$themeKey]?.textUnderlineOffset)}
  ${p => optional('font-family', p.theme[p.$themeKey]?.fontFamily)}

  ${p => optionalPx('padding-top', p.theme[p.$themeKey]?.paddingTop)}
  ${p => optionalPx('padding-right', p.theme[p.$themeKey]?.paddingRight)}
  ${p => optionalPx('padding-bottom', p.theme[p.$themeKey]?.paddingBottom)}
  ${p => optionalPx('padding-left', p.theme[p.$themeKey]?.paddingLeft)}
  ${p => optional('font-weight', p.theme[p.$themeKey]?.fontWeight)}
  ${cssTransition(['background', 'box-shadow', 'border', 'color'], {
    duration: TransitionDuration.Short,
  })}

  ${p =>
    optional(
      'color',
      p.$loading
        ? p.theme[p.$themeKey]?.textColorLoading
        : p.disabled
          ? p.theme[p.$themeKey]?.textColorDisabled
          : p.theme[p.$themeKey]?.textColorActive
    )}
  ${p => (p.$loading ? `& svg { fill: ${p.theme[p.$themeKey]?.textColorLoading}; }` : undefined)}
  ${p =>
    optionalRaw(
      p.$loading
        ? p.theme[p.$themeKey]?.borderColorLoading
        : p.disabled
          ? p.theme[p.$themeKey]?.borderColorDisabled
          : p.theme[p.$themeKey]?.borderColorActive,
      v => `border: solid ${p.theme[p.$themeKey]?.borderWidth}px ${v};`
    )}
  ${p =>
    optional(
      'background',
      p.$loading
        ? p.theme[p.$themeKey]?.backgroundColorLoading
        : p.disabled
          ? p.theme[p.$themeKey]?.backgroundColorDisabled
          : p.theme[p.$themeKey]?.backgroundColorActive
    )}
  &:hover {
    ${p =>
      optional(
        'color',
        p.$loading
          ? p.theme[p.$themeKey]?.textColorLoading
          : p.disabled
            ? p.theme[p.$themeKey]?.textColorDisabled
            : p.theme[p.$themeKey]?.textColorHover
      )}
    ${p =>
      optionalRaw(
        p.$loading
          ? p.theme[p.$themeKey]?.borderColorLoading
          : p.disabled
            ? p.theme[p.$themeKey]?.borderColorDisabled
            : p.theme[p.$themeKey]?.borderColorHover,
        v => `border: solid ${p.theme[p.$themeKey]?.borderWidth}px ${v};`
      )}
    ${p =>
      optional(
        'background',
        p.$loading
          ? p.theme[p.$themeKey]?.backgroundColorLoading
          : p.disabled
            ? p.theme[p.$themeKey]?.backgroundColorDisabled
            : p.theme[p.$themeKey]?.backgroundColorHover
      )}
    ${p => optional('text-decoration', p.theme[p.$themeKey]?.textDecorationHover)}
  }

  &:focus-visible {
    ${p => optional('text-decoration', p.theme[p.$themeKey]?.focusTextDecoration)}
  }

  ${p =>
    optionalRaw(
      p.theme[p.$themeKey]?.focusBorderWidth ?? p.theme[p.$themeKey]?.borderWidth,
      v => `
  &:focus-visible:before {
    content: '';
    position: absolute;
    top: -${v}px;
    right: -${v}px;
    bottom: -${v}px;
    left: -${v}px;
    border: solid ${v}px ${p.theme[p.$themeKey]?.focusBorderColor ?? 'transparent'};
    border-radius: ${v + (p.theme[p.$themeKey]?.borderRadius ?? 0)}px;
  }
  &:focus-visible {
    z-index: ${ZINDEX.ButtonFocusVisible};
  }
  `
    )}

  user-select: ${p => (!p.theme[p.$themeKey]?.enableSelect || p.disabled || p.$loading ? 'none' : 'auto')};
  cursor: ${p => (p.disabled || p.$loading ? 'default' : 'pointer')};
  pointer-events: ${p => (p.disabled || p.$loading ? 'none' : 'all')};
  touch-action: manipulation;
`;

export const Button = createButton({type: 'button', themeType: 'button'});
export const UnthemedButton = createButton({type: 'button', themeType: 'unthemed'});
export const ButtonAsLink = createButton({type: 'button', themeType: 'link'});
export const Link = createButton({type: 'a', themeType: 'link'});
export const UnthemedLink = createButton({type: 'a', themeType: 'unthemed'});
export const LinkAsButton = createButton({type: 'a', themeType: 'link'});
export const NavLink = createButton({type: 'NavLink', themeType: 'link'});
export const UnthemedNavLink = createButton({type: 'NavLink', themeType: 'unthemed'});
export const NavButton = createButton({type: 'NavLink', themeType: 'button'});
